<template>
  <div ref="appRootRef" class="app-container" data-mode="affiliate">
    <div v-if="loading" class="tw:!py-12 tw:!text-center">
      <LoaderCircle class="tw:!mx-auto tw:!h-8 tw:!w-8 tw:!animate-spin od-spinner" />
    </div>
    <Transition v-else name="slide-left" mode="out-in">
      <div
        v-if="store.currentView === 'packages'"
        class="affiliate-country-layout"
        data-focus-view
      >
        <CountryHero
          :country-code="store.countrySlug"
          :country-name="store.countryName"
        />
        <div class="affiliate-packages-card">
          <div v-if="error" class="affiliate-packages-error" role="alert">
            {{ error }}
          </div>
          <PackagesView
            affiliate-layout
            @back="store.currentView = 'packages'"
            @select-package="handleSendPackage"
          />
        </div>
      </div>
      <RegisterView
        v-else-if="store.currentView === 'register'"
        @back="store.currentView = 'packages'"
        @back-home="store.currentView = 'packages'"
        @checkout="handleCheckout"
      />
      <CheckoutView
        v-else-if="store.currentView === 'checkout'"
        @back="store.currentView = 'register'"
        @back-packages="store.currentView = 'packages'"
        @back-home="store.currentView = 'packages'"
        @success="handleSuccess"
      />
      <ProfileView
        v-else-if="store.currentView === 'profile'"
        @back="store.currentView = 'packages'"
      />
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from 'vue'
import { LoaderCircle } from 'lucide-vue-next'
import CountriesData from '~/assets/countriesData.json'
import CountryHero from '~/components/CountryHero.vue'
import PackagesView from '~/components/PackagesView.vue'
import RegisterView from '~/components/RegisterView.vue'
import CheckoutView from '~/components/CheckoutView.vue'
import ProfileView from '~/components/ProfileView.vue'
import { useStore } from '~/composables/widgetMode'
import type { PackageList } from '~/composables/types/esimPackage'
import { sleep } from '~/composables/general'

const store = useStore()
const appRootRef = ref<HTMLElement | null>(null)
const loading = ref(true)
const error = ref('')

onMounted(async () => {
  const region = new URLSearchParams(window.location.search).get('region')?.trim().toUpperCase()
  const country = region
    ? CountriesData.find(item => item.code.toUpperCase() === region)
    : undefined

  if (!country) {
    error.value = 'Destination not found'
    loading.value = false
    return
  }

  try {
    await store.PackageStep(country.code, country.name)
  } catch (err) {
    console.error(err)
    error.value = 'Failed to load destination packages'
  } finally {
    loading.value = false
  }
})

const handleSendPackage = (pkg: PackageList, count: number) => {
  store.RegisterStep(pkg, count)
}

const handleCheckout = (pkg: PackageList, count: number, email: string) => {
  store.CheckoutStep(pkg, count, email)
}

const handleSuccess = (orderNo: string) => {
  store.ProfileStep(orderNo)
}

watch(() => store.currentView, async () => {
  await nextTick()
  await sleep(70)
  const el = appRootRef.value?.querySelector<HTMLElement>('[data-focus-view]')
  if (!el) return
  const top = el.getBoundingClientRect().top + window.scrollY - 80
  window.scrollTo({ top, behavior: 'smooth' })
})
</script>

<style scoped>
.app-container {
  width: 100%;
  min-height: auto;
}

.affiliate-country-layout {
  width: 100%;
  padding-bottom: 2rem;
}

.affiliate-packages-card {
  position: relative;
  z-index: 2;
  width: calc(100% - 4rem);
  max-width: 1080px;
  margin: -6.5rem auto 0;
  padding: 1.25rem;
  box-sizing: border-box;
  border-radius: 2rem;
  background: #fff;
  box-shadow: 0 24px 65px rgba(15, 23, 42, 0.12);
}

.affiliate-packages-error {
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  border: 1px solid #fecaca;
  border-radius: 0.9rem;
  color: #b91c1c;
  background: #fef2f2;
  font-size: 0.875rem;
  font-weight: 600;
  text-align: center;
}

@media (max-width: 768px) {
  .affiliate-country-layout {
    padding-bottom: 1rem;
  }

  .affiliate-packages-card {
    width: calc(100% - 1.5rem);
    margin-top: -4.25rem;
    padding: 0.75rem;
    border-radius: 1.5rem;
  }
}
</style>
